import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DownloadSimple, DeviceMobile } from '@phosphor-icons/react';

export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) return;

    // Check if dismissed recently (don't show for 7 days after dismiss)
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const daysSinceDismiss = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismiss < 7) return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for the install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay (let user explore first)
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show instructions after delay
    if (iOS) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA installed');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50"
      >
        <div className="brutalist-card p-4 bg-white shadow-lg">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full"
            aria-label="Dismiss"
          >
            <X weight="bold" size={20} className="text-gray-500" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#22c55e] flex items-center justify-center flex-shrink-0">
              <DeviceMobile weight="fill" className="text-white" size={24} />
            </div>
            
            <div className="flex-1 pr-6">
              <h3 className="font-fredoka font-bold text-[#1e293b] mb-1">
                Install Bush Rangers
              </h3>
              
              {isIOS ? (
                <p className="text-sm text-[#475569] mb-3">
                  Tap <span className="inline-flex items-center"><DownloadSimple weight="bold" size={14} /></span> then "Add to Home Screen" to install
                </p>
              ) : (
                <p className="text-sm text-[#475569] mb-3">
                  Add to your home screen for quick access, even offline!
                </p>
              )}
              
              {!isIOS && deferredPrompt && (
                <motion.button
                  onClick={handleInstall}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-2 px-4 bg-[#22c55e] text-white rounded-full font-fredoka font-semibold text-sm border-2 border-[#1e293b] shadow-[2px_2px_0_0_#1e293b] hover:shadow-[3px_3px_0_0_#1e293b] transition-shadow flex items-center justify-center gap-2"
                >
                  <DownloadSimple weight="bold" size={18} />
                  Install App
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
